import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../lib/api";
import { fmtIDR, fmtDateTime, today } from "../lib/format";
import { PageHeader, Card, Badge, Modal, Field, Btn, inputCls } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { Plus, Phone, History, MessageSquarePlus, ClipboardList, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import BookingFormModal from "../components/BookingFormModal";

const emptyForm = { name: "", phone: "", email: "", source: "Walk-in", value: 0, notes: "", assigned_to: "", assigned_to_id: "", project_id: "", follow_up_date: "" };

export default function CRM() {
  const { hasPerm, user, projects, projectId } = useAuth();
  const [stages, setStages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState(null);
  const [activities, setActivities] = useState([]);
  const [actNote, setActNote] = useState("");
  const [dragId, setDragId] = useState(null);
  const [bookingGate, setBookingGate] = useState(null);
  const [leadForms, setLeadForms] = useState({});
  const [contacts, setContacts] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [closingGate, setClosingGate] = useState(null);
  const [closingForm, setClosingForm] = useState({ tanggal_akad: "", bank_akad: "", tanggal_jatuh_tempo: "", no_rekening: "" });
  const [updateGate, setUpdateGate] = useState(null);
  const [updateNote, setUpdateNote] = useState("");
  const [updateStage, setUpdateStage] = useState("");

  const isStale = (lead, stageName) => {
    if (!lead.follow_up_date) return false;
    const sn = (stageName || "").toLowerCase();
    if (sn.includes("closing") || sn.includes("lost")) return false;
    const limit = new Date(lead.follow_up_date + "T00:00:00");
    limit.setDate(limit.getDate() + 2);
    return new Date() > limit;
  };

  const [sourceFilter, setSourceFilter] = useState("");
  const [srcReport, setSrcReport] = useState([]);

  const load = useCallback(() => {
    api.get("/crm/stages").then((r) => setStages(r.data));
    api.get(`/crm/leads${projectId ? `?project_id=${projectId}` : ""}`).then(async (r) => {
      setLeads(r.data);
      const forms = {};
      await Promise.all(r.data.map((l) =>
        api.get(`/crm/leads/${l.id}/booking-form`).then((res) => { forms[l.id] = res.data; }).catch(() => {})
      ));
      setLeadForms(forms);
    });
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/crm/report/by-source").then((r) => setSrcReport(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/chat/contacts").then((r) => setContacts(r.data)).catch(() => {}); }, []);

  const openDetail = async (lead) => {
    setDetail(lead);
    const r = await api.get(`/crm/leads/${lead.id}/activities`);
    setActivities(r.data);
  };

  const save = async () => {
    try {
      const payload = { ...form, project_id: form.project_id || null, follow_up_date: form.follow_up_date || null, ...(editing ? { stage_id: editing.stage_id } : {}) };
      if (editing) {
        await api.put(`/crm/leads/${editing.id}`, payload);
        toast.success("Lead diperbarui");
      } else {
        await api.post("/crm/leads", payload);
        toast.success("Prospek baru ditambahkan");
      }
      setShowForm(false); setEditing(null); setForm(emptyForm); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const moveStage = async (leadId, stageId) => {
    const target = stages.find((s) => s.id === stageId);
    if (target && target.name.toLowerCase().includes("closing")) {
      setClosingGate({ leadId, stageId });
      return;
    }
    try {
      await api.put(`/crm/leads/${leadId}/stage`, { stage_id: stageId });
      toast.success("Tahap diperbarui");
      load();
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.detail?.code === "BOOKING_FORM_REQUIRED") {
        const lead = leads.find((l) => l.id === leadId);
        setBookingGate(lead);
      } else {
        toast.error(errMsg(e));
      }
    }
  };

  const submitUpdate = async () => {
    if (!updateNote.trim()) return toast.error("Catatan update wajib diisi");
    try {
      await api.post(`/crm/leads/${updateGate.id}/activities`, { type: "update", note: updateNote, mention_ids: [] });
      setUpdateGate(null);
      if (updateStage && updateStage !== updateGate.stage_id) {
        await moveStage(updateGate.id, updateStage);
      } else {
        toast.success("Update prospek tersimpan");
        load();
      }
    } catch (e) { toast.error(errMsg(e)); }
  };

  const confirmClosing = async () => {    const { tanggal_akad, bank_akad, tanggal_jatuh_tempo, no_rekening } = closingForm;
    if (!tanggal_akad || !bank_akad.trim() || !tanggal_jatuh_tempo || !no_rekening.trim()) {
      return toast.error("Semua data akad wajib diisi");
    }
    try {
      await api.put(`/crm/leads/${closingGate.leadId}/stage`, { stage_id: closingGate.stageId, ...closingForm });
      toast.success("Closing dikonfirmasi — jurnal pendapatan & penerimaan kas terbentuk");
      setClosingGate(null);
      setClosingForm({ tanggal_akad: "", bank_akad: "", tanggal_jatuh_tempo: "", no_rekening: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addActivity = async () => {
    if (!actNote.trim()) return;
    try {
      await api.post(`/crm/leads/${detail.id}/activities`, { type: "note", note: actNote, mention_ids: mentions });
      setActNote(""); setMentions([]);
      const r = await api.get(`/crm/leads/${detail.id}/activities`);
      setActivities(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus lead ini?")) return;
    try {
      const res = await api.delete(`/crm/leads/${id}`);
      if (res.data?.status === "pending_approval") toast.info(res.data.message);
      else toast.success("Lead dihapus");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="crm-page">
      <PageHeader title="CRM Pipeline" subtitle="Kelola prospek dari tahap awal hingga closing — seret kartu antar tahap" testid="crm-header">
        {hasPerm("crm", "create") && (
          <Btn onClick={() => { setForm({ ...emptyForm, assigned_to: user.name, assigned_to_id: user.id }); setEditing(null); setShowForm(true); }} data-testid="add-lead-button">
            <Plus className="w-4 h-4" /> Prospek Baru
          </Btn>
        )}
      </PageHeader>

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="crm-source-tools">
        <select className={`${inputCls} !w-48`} value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} data-testid="crm-source-filter">
          <option value="">Semua Sumber</option>
          {[...new Set(leads.map((l) => l.source).filter(Boolean))].map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      {srcReport.length > 0 && (
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="crm-source-report">
          {srcReport.map((s) => (
            <div key={s.source} className="shrink-0 bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-2" data-testid={`crm-src-${s.source.replace(/\s/g, "-")}`}>
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{s.source}</div>
              <div className="text-sm font-bold text-slate-200">{s.total} lead <span className="text-emerald-400 font-mono text-xs">→ {s.booking} booking</span></div>
              <div className="text-[10px] text-slate-500">{Object.entries(s.by_stage).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-4 overflow-x-auto pb-4">
        {stages.map((stage) => {
          const stageLeads = leads.filter((l) => l.stage_id === stage.id && (!sourceFilter || l.source === sourceFilter));
          return (
            <div
              key={stage.id}
              data-testid={`kanban-column-${stage.name.replace(/\s/g, "-").toLowerCase()}`}
              className="min-w-[260px] w-[260px] shrink-0 bg-slate-900/60 border border-slate-800 rounded-xl p-3"
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { if (dragId) { moveStage(dragId, stage.id); setDragId(null); } }}
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <span className="text-xs font-mono uppercase tracking-wider text-slate-400">{stage.name}</span>
                <Badge tone={stage.color}>{stageLeads.length}</Badge>
              </div>
              <div className="space-y-2 min-h-[80px]">
                {stageLeads.map((lead) => (
                  <div
                    key={lead.id}
                    draggable={hasPerm("crm", "edit")}
                    onDragStart={() => setDragId(lead.id)}
                    onClick={() => openDetail(lead)}
                    data-testid={`kanban-card-${lead.id}`}
                    className="bg-slate-900 border border-slate-700/70 rounded-lg p-3 cursor-pointer hover:border-emerald-500/50 transition-colors"
                  >
                    <div className="text-sm font-medium text-slate-200">{lead.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1 mt-1"><Phone className="w-3 h-3" />{lead.phone || "-"}</div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs font-mono text-emerald-400">{fmtIDR(lead.value)}</span>
                      <span className="text-[10px] text-slate-500">{lead.source}</span>
                    </div>
                    {leadForms[lead.id] && (
                      <div className="mt-1.5 flex items-center gap-1 text-[10px] text-cyan-300" data-testid={`lead-has-form-${lead.id}`}>
                        <ClipboardList className="w-3 h-3" /> Formulir booking: {leadForms[lead.id].blok_nomor_unit}
                      </div>
                    )}
                    {isStale(lead, stage.name) && (
                      <div className="mt-1.5 flex items-center gap-1 text-[10px] font-medium text-rose-400" data-testid={`lead-stale-${lead.id}`}>
                        <AlertTriangle className="w-3 h-3" /> Perlu follow-up — lewat jadwal ({lead.follow_up_date}) +2 hari
                      </div>
                    )}
                    <div className="mt-2 flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                      <select className="flex-1 min-w-0 bg-slate-950 border border-slate-700 rounded px-1.5 py-1 text-[10px] text-slate-300" value=""
                        onChange={(e) => { if (e.target.value) moveStage(lead.id, e.target.value); }} data-testid={`lead-move-${lead.id}`}>
                        <option value="">Pindah ke...</option>
                        {stages.filter((s) => s.id !== stage.id).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                      <button className="text-[10px] text-cyan-300 border border-cyan-500/40 rounded px-2 py-1 hover:bg-cyan-500/10 transition-colors shrink-0"
                        onClick={() => { setUpdateGate(lead); setUpdateNote(""); setUpdateStage(""); }} data-testid={`lead-update-${lead.id}`}>Update</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? "Edit Lead" : "Prospek Baru"} testid="lead-form-modal">
        <div className="space-y-3">
          <Field label="Nama Lengkap"><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name-input" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Telepon"><input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone-input" /></Field>
            <Field label="Email"><input className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lead-email-input" /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Sumber">
              <select className={inputCls} value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="lead-source-select">
                {["Walk-in", "Instagram", "Facebook", "Referral", "Pameran", "Website", "ABAD JAYA LIVING", "Lainnya"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Estimasi Nilai (Rp)"><input type="number" className={inputCls} value={form.value} onChange={(e) => setForm({ ...form, value: +e.target.value })} data-testid="lead-value-input" /></Field>
          </div>
          <Field label="Catatan"><textarea className={inputCls} rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="lead-notes-input" /></Field>
          <Field label="Tugaskan ke Sales">
            <select className={inputCls} value={form.assigned_to_id || ""} onChange={(e) => { const u = contacts.find((c) => c.id === e.target.value); setForm({ ...form, assigned_to_id: e.target.value, assigned_to: u ? u.name : (e.target.value ? form.assigned_to : "") }); }} data-testid="lead-assign-select">
              <option value="">Belum ditugaskan...</option>
              {user?.role === "Sales" && <option value={user.id}>{user.name} (saya)</option>}
              {contacts.filter((c) => c.role === "Sales").map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Proyek / Perumahan">
              <select className={inputCls} value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} data-testid="lead-project-select">
                <option value="">Umum</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Jadwal Follow-up"><input type="date" className={inputCls} value={form.follow_up_date} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })} data-testid="lead-followup-input" /></Field>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Btn tone="ghost" onClick={() => setShowForm(false)}>Batal</Btn>
            <Btn onClick={save} data-testid="lead-save-button">Simpan</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail ? `Lead: ${detail.name}` : ""} testid="lead-detail-modal">
        {detail && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div><span className="text-slate-500 text-xs">Telepon</span><div className="text-slate-200">{detail.phone || "-"}</div></div>
              <div><span className="text-slate-500 text-xs">Nilai</span><div className="font-mono text-emerald-400">{fmtIDR(detail.value)}</div></div>
              <div><span className="text-slate-500 text-xs">Sumber</span><div className="text-slate-200">{detail.source}</div></div>
              <div><span className="text-slate-500 text-xs">Sales</span><div className="text-slate-200">{detail.assigned_to || "-"}</div></div>
            </div>
            {leadForms[detail.id] && (
              <div className="bg-cyan-500/5 border border-cyan-500/30 rounded-lg p-3 space-y-1.5" data-testid="lead-booking-form-summary">
                <div className="text-[10px] font-mono uppercase tracking-wider text-cyan-400 flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Formulir Booking Konsumen</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">Unit</span> <span className="font-mono text-slate-200">{leadForms[detail.id].blok_nomor_unit}</span></div>
                  <div><span className="text-slate-500">Perumahan</span> <span className="text-slate-200">{leadForms[detail.id].nama_perumahan}</span></div>
                  <div><span className="text-slate-500">NIK</span> <span className="font-mono text-slate-200">{leadForms[detail.id].nik_pemohon}</span></div>
                  <div><span className="text-slate-500">Cabang Bank</span> <span className="text-slate-200">{leadForms[detail.id].cabang_bank}</span></div>
                  <div className="col-span-2"><span className="text-slate-500">Harga</span> <span className="font-mono text-emerald-400">{fmtIDR(leadForms[detail.id].harga_jual_rumah)}</span> <span className="text-slate-500 italic">({leadForms[detail.id].harga_jual_rumah_terbilang})</span></div>
                </div>
                <div className="text-[11px] text-cyan-300">Dokumen auto-generate: {leadForms[detail.id].documents_generated ?? 0} berkas (lihat Modul Dokumen)</div>
              </div>
            )}
            {hasPerm("crm", "edit") && (
              <div className="flex gap-2">
                <Btn tone="ghost" onClick={() => { setForm({ ...emptyForm, ...detail, assigned_to_id: detail.assigned_to_id || "", project_id: detail.project_id || "", follow_up_date: detail.follow_up_date || "" }); setEditing(detail); setDetail(null); setShowForm(true); }} data-testid="lead-edit-button">Edit</Btn>
                {hasPerm("crm", "delete") && <Btn tone="danger" onClick={() => { remove(detail.id); setDetail(null); }} data-testid="lead-delete-button">Hapus</Btn>}
              </div>
            )}
            <div>
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-slate-400 mb-2"><History className="w-3.5 h-3.5" /> Log Aktivitas</div>
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {activities.length === 0 && <p className="text-slate-500 text-sm">Belum ada aktivitas.</p>}
                {activities.map((a) => (
                  <div key={a.id} className="text-xs bg-slate-950 border border-slate-800 rounded-lg p-2.5" data-testid={`activity-${a.id}`}>
                    <div className="text-slate-300">{a.note}</div>
                    <div className="text-slate-500 mt-1">{a.by} &middot; {fmtDateTime(a.at)}</div>
                  </div>
                ))}
              </div>
              {hasPerm("crm", "create") && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Tag sales:</span>
                    <select className="bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-xs text-slate-200" value=""
                      onChange={(e) => { if (e.target.value && !mentions.includes(e.target.value)) setMentions([...mentions, e.target.value]); }} data-testid="mention-select">
                      <option value="">Pilih user...</option>
                      {contacts.filter((c) => c.id !== user?.id).map((c) => <option key={c.id} value={c.id}>{c.name} ({c.role})</option>)}
                    </select>
                    {mentions.map((mid) => {
                      const mu = contacts.find((c) => c.id === mid);
                      return <span key={mid} className="text-[10px] font-mono bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 rounded-full px-2 py-0.5 cursor-pointer" onClick={() => setMentions(mentions.filter((x) => x !== mid))} data-testid={`mention-chip-${mid}`}>@{mu?.name || mid} ×</span>;
                    })}
                  </div>
                  <div className="flex gap-2">
                    <input className={inputCls} placeholder="Tulis catatan follow-up... (user yang di-tag mendapat notifikasi)" value={actNote} onChange={(e) => setActNote(e.target.value)} data-testid="activity-note-input" />
                    <Btn onClick={addActivity} data-testid="activity-add-button"><MessageSquarePlus className="w-4 h-4" /></Btn>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
      {bookingGate && (
        <BookingFormModal
          lead={bookingGate}
          onClose={() => setBookingGate(null)}
          onDone={() => { setBookingGate(null); load(); }}
        />
      )}
      <Modal open={!!closingGate} onClose={() => setClosingGate(null)} title="Konfirmasi Closing — Data Akad (Wajib)" testid="closing-form-modal">
        <div className="space-y-3">
          <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg px-3 py-2 text-xs text-amber-300" data-testid="closing-gate-notice">
            Memindahkan lead ke Closing akan membentuk jurnal pendapatan (harga − diskon) dan jurnal penerimaan kas agregat atas seluruh DP/cicilan yang sudah masuk.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Tanggal Akad *"><input type="date" className={inputCls} value={closingForm.tanggal_akad} onChange={(e) => setClosingForm({ ...closingForm, tanggal_akad: e.target.value })} data-testid="closing-tanggal-akad" /></Field>
            <Field label="Bank Akad *"><input className={inputCls} placeholder="Contoh: BTN Cabang Stabat" value={closingForm.bank_akad} onChange={(e) => setClosingForm({ ...closingForm, bank_akad: e.target.value })} data-testid="closing-bank-akad" /></Field>
            <Field label="Tanggal Jatuh Tempo *"><input type="date" className={inputCls} value={closingForm.tanggal_jatuh_tempo} onChange={(e) => setClosingForm({ ...closingForm, tanggal_jatuh_tempo: e.target.value })} data-testid="closing-jatuh-tempo" /></Field>
            <Field label="No Rekening *"><input className={inputCls} placeholder="Nomor rekening pembayaran" value={closingForm.no_rekening} onChange={(e) => setClosingForm({ ...closingForm, no_rekening: e.target.value })} data-testid="closing-no-rekening" /></Field>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Btn tone="ghost" onClick={() => setClosingGate(null)}>Batal</Btn>
            <Btn onClick={confirmClosing} data-testid="closing-confirm-button">Konfirmasi Closing</Btn>
          </div>
        </div>
      </Modal>
      <Modal open={!!updateGate} onClose={() => setUpdateGate(null)} title={updateGate ? `Update Prospek — ${updateGate.name}` : ""} testid="lead-update-modal">
        <div className="space-y-3">
          <Field label="Catatan Update (wajib)">
            <textarea className={inputCls} rows={3} placeholder="Contoh: sudah dihubungi, minta survei minggu depan..." value={updateNote} onChange={(e) => setUpdateNote(e.target.value)} data-testid="update-note-input" />
          </Field>
          <Field label="Pindahkan ke Tahap (opsional)">
            <select className={inputCls} value={updateStage} onChange={(e) => setUpdateStage(e.target.value)} data-testid="update-stage-select">
              <option value="">Tetap di tahap sekarang</option>
              {stages.filter((s) => s.id !== updateGate?.stage_id).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <p className="text-[11px] text-slate-500">Pilih tahap "Lost" untuk menutup prospek, atau "Closing" (akan diminta data akad).</p>
          <div className="flex justify-end gap-2 pt-1">
            <Btn tone="ghost" onClick={() => setUpdateGate(null)}>Batal</Btn>
            <Btn onClick={submitUpdate} data-testid="update-submit-button">Simpan Update</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
